import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { LevelHalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofWallSystem } from "building-types/roofs/roofWallSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { RoofWallMode } from "building-types/settings/settingsTypesEnum";
import type { WallPlaneMasses, WallPlane } from "building-types/walls2/wallPlane";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { WallLayer } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { DIST_EPSILON } from "geom/core";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import { WallDatumAbstractionProvider } from "../elements/datums/datumAbstractionProviders";
import getTargetedPorchSettings from "../elements/porch/getTargetedPorchSettings";
import { buildSubRoofWallDiscardBSPs, collectRoofWallCuts } from "../elements/roofs/roofWalls";
import { makeRoofWallSystems } from "../elements/roofs/roofWallSystem";
import {
  makeRoofBlock,
  makeRoofRooms,
  makeRoofWallCutHalfEdgeMap,
  makeRoofWallLevel,
  makeRoofWallSystemPerimetersFromExactOutline,
} from "../elements/roofs/roofWallSystemVirtualLevel";
import { getEaveDepth } from "../elements/roofTrimUtility";
import { subdomainHasRoofVolumeParentAndNotRoomParent } from "../elements/walls2/wallPlaneSubdomainRoomFilter";
import { wallLayersAddFromRoofs, wallLayerTable } from "../elements/walls/wallLayers";
import { makeWallSystem } from "../elements/walls/wallSystem";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { getRoofPlanarGraphPerimeterInfo } from "../utils/planarGraphPerimeters";
import { generateLevelWallMassesFromWallPlanes } from "./generateWallMasses2";

const ROOF_WALL_DISCARD_VOID_OFFSET = 20;

function select({
  levels,
  roofSystemLevels,
  roofSystems: roofSystems,
  windowPlacements: windows,
  doorPlacements: doors,
  wallCuts,
  wallLayers,
  wallPlaneMasses,
  wallPlanes,
  buildingGraph,
  settings,
  settingsSets,
  optionChoices,
  walls,
  rooms,
  blocks,
  facades,
  facadeSweepStrata,
  facadeSpanStrata,
  datumAbstractions,
  mirror,
  enabledFeatures,
  doc: {
    settings: docSettings,
    entitySettings,
    foundationComponents,
    foundationAssemblies,
    rooms: docRooms,
    framingComponents,
    slabComponents,
    sheathingComponents,
    profiles,
  },
}: Building) {
  return {
    levels,
    roofSystemLevels,
    roofSystems,
    windows,
    doors,
    wallCuts,
    wallLayers,
    wallPlaneMasses,
    wallPlanes,
    buildingGraph,
    wallSettings: settings.wall,
    wallCatalogSettings: settings.wallCatalog,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles,
      profileSettings: settings.profile,
    },
    exteriorTrimSettings: settings.exteriorTrim,
    porchSettings: settings.porch,
    rearPorchSettings: settings.rearPorch,
    datumAbstractions,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings: settings.foundation,
    foundationComponents,
    foundationAssemblies,
    facades,
    walls,
    rooms,
    docRooms,
    blocks,
    facadeSweepStratum: facadeSweepStrata,
    facadeSpanStratum: facadeSpanStrata,
    mirror,
    settingsSets,
    optionChoices,
    docSettings,
    entitySettings,
    settings,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  profileSource: "shallow",
});

function generate({
  levels,
  roofSystemLevels,
  roofSystems,
  windows,
  doors,
  wallCuts,
  wallSettings,
  wallLayers,
  wallPlaneMasses,
  wallPlanes,
  buildingGraph,
  wallCatalogSettings,
  profileSource,
  exteriorTrimSettings,
  porchSettings,
  rearPorchSettings,
  framingComponents,
  slabComponents,
  sheathingComponents,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  walls,
  blocks,
  rooms,
  docRooms,
  facades,
  datumAbstractions,
  facadeSpanStratum,
  facadeSweepStratum,
  mirror,
  settingsSets,
  optionChoices,
  docSettings,
  entitySettings,
  settings,
  enabledFeatures,
}: ReturnType<typeof select>): Pick<Building, "roofSystems" | "wallLayers" | "datumAbstractions" | "issues"> {
  const issues: BuildingGenerationIssue[] = [];

  if (enabledFeatures.wallPlanes) {
    return generateRoofLevelWallMassesFromWallPlanes({
      levels,
      roofSystems,
      wallPlaneMasses,
      wallPlanes,
      windows,
      doors,
      wallCuts,
      wallCatalogSettings,
      wallLayers,
      buildingGraph,
      datumAbstractions,
      mirror,
      issues,
    });
  }

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSystemSettingsFromSettingsConfig(foundationSettings),
  );

  const newRoofs: RWTable<GeneratedRoofSystem> = {};
  const updatedDatumAbstractions: RWTable<DatumAbstraction> = {
    ...datumAbstractions,
  };
  const targetedPorchSettings = getTargetedPorchSettings(
    { settingsSets, optionChoices, settings, rooms },
    { settings: docSettings, entitySettings },
  );

  const roofWallCuts = collectRoofWallCuts(windows, doors, wallCuts);

  const roofWallDiscardBSPs = buildSubRoofWallDiscardBSPs(
    levels,
    walls,
    rooms,
    porchSettings,
    rearPorchSettings,
    targetedPorchSettings,
    ROOF_WALL_DISCARD_VOID_OFFSET,
  );

  let newWallLayers: WallLayer[] = [];

  for (const roofSystemId in roofSystems) {
    const roof = roofSystems[roofSystemId];

    const roofWallSystem = makeRoofWallSystems({
      levels,
      docRooms,
      walls,
      blocks,
      roofSystem: roof,
      wallSettings,
      wallCatalogSettings,
      facades,
      datumAbstractions: updatedDatumAbstractions,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSettings,
      foundationComponents: finalizedFoundationComponents,
      foundationAssemblies,
      profileSource,
      exteriorTrimSettings,
      facadeSpanStratum,
      facadeSweepStratum,
      roofWallCuts,
      roofWallDiscardBSPs,
      roofSystemLevels,
      mirror,
      issues,
    });

    newWallLayers = wallLayersAddFromRoofs(newWallLayers, roofWallSystem.roofs);

    newRoofs[roofSystemId] = roofWallSystem;
  }

  const newWallLayersTable = newWallLayers.reduce(wallLayerTable, { ...wallLayers });

  return { roofSystems: newRoofs, wallLayers: newWallLayersTable, datumAbstractions: updatedDatumAbstractions, issues };
}

function generateRoofLevelWallMassesFromWallPlanes({
  levels,
  roofSystems,
  wallPlaneMasses,
  wallPlanes,
  windows,
  doors,
  wallCuts,
  wallCatalogSettings,
  wallLayers,
  buildingGraph,
  datumAbstractions,
  mirror,
  issues,
}: {
  levels: Table<Level>;
  roofSystems: RWTable<GeneratedRoofSystem>;
  wallPlaneMasses: WallPlaneMasses;
  wallPlanes: Table<WallPlane>;
  windows: Table<GeneratedWindowPlacement>;
  doors: Table<GeneratedDoorPlacement>;
  wallCuts: LevelHalfEdgeAssociationTable<WallCut>;
  wallCatalogSettings: Building["settings"]["wallCatalog"];
  wallLayers: Table<WallLayer>;
  buildingGraph: BuildingGraph;
  datumAbstractions: RWTable<DatumAbstraction>;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}): Pick<Building, "roofSystems" | "wallLayers" | "datumAbstractions" | "issues"> {
  const roofSystemsWithWallSystems: RWTable<GeneratedRoofSystem> = {};
  let newWallLayers: WallLayer[] = [];

  const roofWallCuts = collectRoofWallCuts(windows, doors, wallCuts);

  for (const roofSystemId in roofSystems) {
    const roofSystemWithWalls = makeRoofWallSystems2({
      levels,
      roofSystem: roofSystems[roofSystemId],
      wallPlaneMasses,
      wallPlanes,
      wallCatalogSettings,
      roofWallCuts,
      buildingGraph,
      datumAbstractions,
      mirror,
      issues,
    });

    roofSystemsWithWallSystems[roofSystemId] = roofSystemWithWalls;
    newWallLayers = wallLayersAddFromRoofs(newWallLayers, roofSystemWithWalls.roofs);
  }

  const newWallLayersTable = newWallLayers.reduce(wallLayerTable, { ...wallLayers });
  return { roofSystems: roofSystemsWithWallSystems, wallLayers: newWallLayersTable, datumAbstractions, issues };
}

function makeRoofWallSystems2({
  levels,
  roofSystem,
  wallPlaneMasses,
  wallPlanes,
  wallCatalogSettings,
  roofWallCuts,
  buildingGraph,
  datumAbstractions,
  mirror,
  issues,
}: {
  levels: Table<Level>;
  roofSystem: GeneratedRoofSystem;
  wallPlaneMasses: WallPlaneMasses;
  wallPlanes: Table<WallPlane>;
  wallCatalogSettings: Building["settings"]["wallCatalog"];
  roofWallCuts: WallOpening[];
  buildingGraph: BuildingGraph;
  datumAbstractions: RWTable<DatumAbstraction>;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}): GeneratedRoofSystem {
  const roofWallPlaneMasses = wallPlaneMasses.masses.filter((mass) =>
    subdomainHasRoofVolumeParentAndNotRoomParent(mass.miterEdge.subdomain, buildingGraph),
  );

  const roofs: RWTable<GeneratedRoofSystem["roofs"][string]> = {};
  const roofWallLevels: RWTable<RoofWallLevel> = {};

  const roofLevels: RWTable<Level> = {};
  const roofLevelPlanarGraphs: RWTable<PolygonPlanarGraph> = {};
  const roofLevelsRooms: RWTable<Room> = {};
  const roofLevelsRoomsByLevel: RWTable<string[]> = {};
  const roofLevelsRoomLevelIndices: RWTable<number> = {};

  for (const roofId in roofSystem.roofs) {
    const roof = roofSystem.roofs[roofId];
    const roofWallLevel = makeRoofWallSystemLevel(roof, levels);
    if (!roofWallLevel) {
      continue;
    }

    // merge all to the input tables
    const {
      level: roofLevel,
      levelPlanarGraph: roofPlanarGraph,
      levelRoomsByLevel: roofRoomsByLevel,
      levelRoomLevelIndices: roofRoomLevelIndices,
      levelRooms: roofRooms,
    } = roofWallLevel;

    roofWallLevels[roofId] = roofWallLevel;
    roofLevels[roofLevel.id] = roofLevel;
    roofLevelPlanarGraphs[roofLevel.id] = roofPlanarGraph;
    roofLevelsRoomsByLevel[roofLevel.id] = roofRoomsByLevel[roofLevel.id];
    Object.assign(roofLevelsRoomLevelIndices, roofRoomLevelIndices);
    Object.assign(roofLevelsRooms, roofRooms);
  }

  const roofWallMassesResult = generateLevelWallMassesFromWallPlanes({
    levels: roofLevels,
    levelPlanarGraphs: roofLevelPlanarGraphs,
    buildingGraph,
    wallPlaneMasses: roofWallPlaneMasses,
    wallPlanes,
    issues,
    rooms: roofLevelsRooms,
    roomsByLevel: roofLevelsRoomsByLevel,
    roomLevelIndices: roofLevelsRoomLevelIndices,
  });

  // rebuild and reassign to the roofs
  for (const roofId in roofWallLevels) {
    const roof = roofSystem.roofs[roofId];
    const { level, levelPlanarGraph, levelAttributes, levelRooms } = roofWallLevels[roofId];
    const wallMassSystem = roofWallMassesResult.wallMasses[level.id];
    if (!wallMassSystem) {
      roofs[roofId] = roof;
      continue;
    }

    const roofWallMasses: Table<WallMassSystem> = { [level.id]: wallMassSystem };
    const { halfEdgeToWallCutSet } = makeRoofWallCutHalfEdgeMap(levelPlanarGraph, level, roofWallCuts);
    const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(
      { [level.id]: level },
      { [level.id]: levelAttributes },
      datumAbstractions,
    );

    const wallSystem = makeWallSystem({
      wallDatumAbstractionProvider,
      wallMasses: roofWallMasses,
      halfEdgeToWallCutSet,
      levelId: level.id,
      wallCatalogSettings,
      mirror,
      issues,
    });

    const roofWallSystem: RoofWallSystem = {
      wallMassSystem,
      wallSystem,
      planarGraph: levelPlanarGraph,
      planarGraphPerimeterInfo: getRoofPlanarGraphPerimeterInfo(levelPlanarGraph, mirror),
      levels: { [level.id]: level },
      levelsAttributes: { [level.id]: levelAttributes },
      rooms: levelRooms,
      levelId: level.id,
      edges: {},
      facadeTrimOffsetZ: 0,
    };

    roofs[roofId] = {
      ...roof,
      roofWallSystem,
    };
  }

  return {
    ...roofSystem,
    roofs,
  };
}

interface RoofWallLevel {
  level: Level;
  levelPlanarGraph: PolygonPlanarGraph;
  levelAttributes: LevelAttributes;
  levelRooms: Table<Room>;
  levelRoomsByLevel: Table<string[]>;
  levelRoomLevelIndices: Table<number>;
}

/**
 * @TODO  WALLS V2 - this is currently a stripped out version of the same code that runs in roofWallSystem.ts,
 * but it's possible we don't need all the complexity here since the purpose of this returned result
 * is to collect the walls v2 miter masses.
 */
function makeRoofWallSystemLevel(roof: GeneratedRoof, levels: Table<Level>): RoofWallLevel | null {
  if (roof.isDormer) {
    return null;
  }

  const sourceLevel = levels[roof.levelId];
  if (!sourceLevel) {
    return null;
  }

  const { peakHeight, baseHeight } = roof.roofVolume;

  const eaveDepth = getEaveDepth(roof.roofSettings);
  const heelHeightRefOffset = roof.roofVolume.heelHeightOffsets.refOffset;
  const baseHeelHeight = Math.min(eaveDepth, Math.max(heelHeightRefOffset, 0));
  const negativeHeelHeight = Math.min(heelHeightRefOffset + eaveDepth, 0);
  const floorElevation = Math.max(sourceLevel.floorElevation, baseHeight - baseHeelHeight + negativeHeelHeight);
  const wallTop = Math.max(baseHeight - baseHeelHeight - floorElevation, DIST_EPSILON);
  const roofBottom = wallTop;
  const roofTop = Math.max(peakHeight - floorElevation, roofBottom);
  const roofLevel = makeRoofWallLevel(floorElevation, wallTop, roofBottom, roofTop);

  const roofWallPerimeters = makeRoofWallSystemPerimetersFromExactOutline(roof.roofVolume);
  if (roofWallPerimeters.length === 0) {
    return null;
  }

  const roofPlanarGraph = polygonPlanarGraphNew(roofWallPerimeters.map(({ perimeter }) => perimeter));
  const roofBlock = makeRoofBlock(roofLevel);
  const roofRoomData = makeRoofRooms({
    roofLevels: { [roofLevel.id]: roofLevel },
    roofLevel,
    roofBlock,
    roofWallPerimeters,
    roofWallMode: roof.roofSettings.roofWallMode ?? RoofWallMode.INTERIOR,
  });

  return {
    level: roofLevel,
    levelPlanarGraph: roofPlanarGraph,
    levelAttributes: { windowHeadHeight: 0 },
    levelRooms: roofRoomData.rooms,
    levelRoomsByLevel: roofRoomData.roomsByLevel,
    levelRoomLevelIndices: roofRoomData.roomLevelIndices,
  };
}

export default memo(select, generate, compare);
